import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class LeadPipelineStageReadOnly extends ModelObject<LeadPipelineStageReadOnly> {

    /**
    * The order of this pipeline stage
    */
    public ordering: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.ordering = jsonSerializedObject.ordering;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.ordering = this.ordering;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "ordering", rule: (v: any) => v == null || v >= -2147483648 || "Ordering is not greater than minimum allowed value: -2147483648"},
            {field: "ordering", rule: (v: any) => v == null || v <= 2147483647 || "Ordering is above maximum allowed value: 2147483647"},
    ];
}


export class LeadPipelineStage extends ModelObjectWithDirtyCheck<LeadPipelineStage> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Lead pipeline stage name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public readOnly: LeadPipelineStageReadOnly = new LeadPipelineStageReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.readOnly = new LeadPipelineStageReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "LeadPipelineStage.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class LeadPipelineStageService extends ServiceBase {

  public async getAllLeadPipelineStages(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(LeadPipelineStage)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.leadPipelineStages.map((v: any) => new LeadPipelineStage(v));;
    return this.makeJsonRequest("leadPipelineStage/v1/getAllLeadPipelineStages", req, false, extractor, opts);
  };

  public async getLeadPipelineStage(leadPipelineStageId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<LeadPipelineStage> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadPipelineStageId:leadPipelineStageId.toString(),
    };
    const extractor = (response:any) => new LeadPipelineStage(response.leadPipelineStage);;
    return this.makeJsonRequest("leadPipelineStage/v1/getLeadPipelineStage", req, false, extractor, opts);
  };

  public async getLeadPipelineStages(leadPipelineStageIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(LeadPipelineStage)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadPipelineStageIds:leadPipelineStageIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.leadPipelineStages.map((v: any) => new LeadPipelineStage(v));;
    return this.makeJsonRequest("leadPipelineStage/v1/getLeadPipelineStages", req, false, extractor, opts);
  };

  public async saveLeadPipelineStage(leadPipelineStage: LeadPipelineStage, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadPipelineStage:leadPipelineStage.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("leadPipelineStage/v1/saveLeadPipelineStage", req, true, extractor, opts);
  };

  public async updateLeadPipelineStageOrdering(leadPipelineStageIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadPipelineStageIds:leadPipelineStageIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("leadPipelineStage/v1/updateLeadPipelineStageOrdering", req, true, extractor, opts);
  };

  public async deleteLeadPipelineStage(leadPipelineStageId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    leadPipelineStageId:leadPipelineStageId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("leadPipelineStage/v1/deleteLeadPipelineStage", req, true, extractor, opts);
  };
}

export const leadPipelineStageService = new LeadPipelineStageService();